/**
 * Orders Redux Slice
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

const initialState = {
  orderBook: [],
  tradeBook: [],
  pendingOrders: [],
  orderUpdates: [],
  loading: false,
  error: null,
};

// Fetch order book
export const fetchOrderBook = createAsyncThunk(
  'orders/fetchOrderBook',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await api.get('/orders/book', {
        headers: { Authorization: `Bearer ${auth.userSession}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

// Fetch trade book
export const fetchTradeBook = createAsyncThunk(
  'orders/fetchTradeBook',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await api.get('/orders/trades', {
        headers: { Authorization: `Bearer ${auth.userSession}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

// Place order
export const placeOrder = createAsyncThunk(
  'orders/placeOrder',
  async (orderData, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await api.post('/orders/place', orderData, {
        headers: { Authorization: `Bearer ${auth.userSession}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

// Modify order
export const modifyOrder = createAsyncThunk(
  'orders/modifyOrder',
  async (modifyData, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await api.post('/orders/modify', modifyData, {
        headers: { Authorization: `Bearer ${auth.userSession}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

// Cancel order
export const cancelOrder = createAsyncThunk(
  'orders/cancelOrder',
  async (brokerOrderId, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await api.post('/orders/cancel', { brokerOrderId }, {
        headers: { Authorization: `Bearer ${auth.userSession}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    addOrderUpdate: (state, action) => {
      state.orderUpdates.unshift(action.payload);
      // Update order book with real-time data
      const orderIndex = state.orderBook.findIndex(
        order => order.brokerOrderId === action.payload.norenordno
      );
      if (orderIndex !== -1) {
        state.orderBook[orderIndex] = {
          ...state.orderBook[orderIndex],
          orderStatus: action.payload.status,
          filledQuantity: action.payload.fillshares,
          averageTradedPrice: action.payload.avgprc,
        };
      }
    },
    clearOrderUpdates: (state) => {
      state.orderUpdates = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch Order Book
    builder.addCase(fetchOrderBook.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchOrderBook.fulfilled, (state, action) => {
      state.loading = false;
      state.orderBook = action.payload.data || [];
    });
    builder.addCase(fetchOrderBook.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.error || 'Failed to fetch order book';
    });

    // Fetch Trade Book
    builder.addCase(fetchTradeBook.fulfilled, (state, action) => {
      state.tradeBook = action.payload.data || [];
    });

    // Place Order
    builder.addCase(placeOrder.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(placeOrder.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(placeOrder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.error || 'Failed to place order';
    });

    // Cancel Order
    builder.addCase(cancelOrder.fulfilled, (state) => {
      state.loading = false;
    });
  },
});

export const { addOrderUpdate, clearOrderUpdates } = ordersSlice.actions;
export default ordersSlice.reducer;
